/*
 * <sd-legal-nav> — the policy page's sticky section menu (sections/main-policy.liquid).
 *
 * · Scroll-spy: the active link is the LAST section whose top has passed a line a
 *   little below the header. A position check on scroll (rAF-throttled) rather than
 *   an IntersectionObserver: with sections of very different lengths an observer's
 *   "which one is intersecting" is ambiguous, and a short final section could never
 *   become active. At the very bottom of the page the last section wins outright.
 * · Click: smooth-scrolls (instant under reduced motion) and updates the hash without
 *   a jump. The offset comes from each section's CSS `scroll-margin-top`.
 * Without JS the links are plain in-page anchors.
 */
class SdLegalNav extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('.sd-legal__link')];
    this.sections = this.links
      .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
      .filter(Boolean);
    if (!this.sections.length) return;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.addEventListener('click', (event) => {
      const link = event.target.closest('.sd-legal__link');
      if (!link) return;
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', link.hash);
      this.setActive(this.sections.indexOf(target));
      // Hold the clicked item while the smooth scroll passes the sections above it.
      this.lockUntil = performance.now() + 900;
    });

    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  update() {
    if (this.lockUntil && performance.now() < this.lockUntil) return;
    const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sd-header-h')) || 64;
    const line = headerH + window.innerHeight * 0.25;
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    let index = 0;
    if (atBottom) {
      index = this.sections.length - 1;
    } else {
      this.sections.forEach((section, i) => {
        if (section.getBoundingClientRect().top <= line) index = i;
      });
    }
    this.setActive(index);
  }

  setActive(index) {
    if (index === this.active) return;
    this.active = index;
    this.links.forEach((link, i) => {
      const on = i === index;
      link.classList.toggle('is-active', on);
      if (on) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('sd-legal-nav')) customElements.define('sd-legal-nav', SdLegalNav);
